import { useState, useEffect, useCallback } from "react";
import {
  getLlmVisibility, updateLlmVisConfig, runLlmVisibilityScan, getLlmVisScan, dfsBalance, rankCountries,
  getCrawlability, runCrawlabilityCheck, explainCrawlability, getCrawlabilityCheck,
  type LlmVisConfig, type LlmVisScanMeta, type LlmVisScanResult,
  type LlmVisKnowledgeMode, type EntityKnowledgeCard, type DfsBalance, type RankCountry,
  type CrawlabilityResult, type CrawlabilityCheckMeta,
} from "../lib/authClient";
import { siteKey } from "../lib/siteData";
import { showAlert, showConfirm } from "../lib/dialog";
import { CityPicker } from "./CityPicker";

// LLM Visibility Scanner — shows what each AI model knows about your business + up to 5 competitors as
// entities (training-data-only or live-search), a visibility matrix of who the models recommend for
// "best {type} in {area}", and recommendations for out-ranking those competitors. On-demand (a scan
// is credit-heavy). Backed by /api/llm-visibility (DataForSEO model probes + Claude analysis).

interface Props { siteUrl?: string; siteBrand?: string; refreshToken?: number }

const PLATFORM_LABEL: Record<string, string> = {
  chatgpt: "ChatGPT", claude: "Claude", gemini: "Gemini", perplexity: "Perplexity", google_ai_overview: "Google AI Overview",
};
const platLabel = (p: string) => PLATFORM_LABEL[p] ?? p;
const LOW_BALANCE_USD = 10;
const usd = (n: number) => n.toLocaleString(undefined, { style: "currency", currency: "USD", maximumFractionDigits: 2 });
const fmtDateTime = (iso: string) => { const d = new Date(iso); return isNaN(d.getTime()) ? iso.slice(0, 16) : d.toLocaleString("en-US", { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" }); };
const domainOf = (u: string) => { try { return new URL(u.startsWith("http") ? u : `https://${u}`).hostname.replace(/^www\./, ""); } catch { return u; } };

type RunResult = { result: LlmVisScanResult; scans: LlmVisScanMeta[] };
const visRuns = new Map<string, Promise<RunResult>>(); // siteKey → in-flight scan (survives tab switch)
type CrawlRunResult = { result: CrawlabilityResult; checkId: string; checks: CrawlabilityCheckMeta[] };
const crawlRuns = new Map<string, Promise<CrawlRunResult>>(); // siteKey → in-flight technical check (survives tab switch)
const MONO = "'Roboto Mono',ui-monospace,SFMono-Regular,Menlo,monospace";
const RENDER_LABEL: Record<string, string> = { ssr: "Server-rendered", hybrid: "Hybrid (some JS-dependent)", "js-only": "JavaScript-only (SPA)", unknown: "Unknown" };

type Loc = { name: string; code: number } | null;
type CompRow = { name: string; website: string };
const EMPTY_COMP: CompRow = { name: "", website: "" };
const DEFAULT_COUNTRY = "United States";
// A DataForSEO location_name is "City,Region,Country" — the last segment is the country.
const countryOf = (loc: string) => { const p = (loc || "").split(","); return p.length > 1 ? p[p.length - 1].trim() : ""; };
// Default visibility-matrix questions, mirroring recommendationPrompts() in server/llmVisibility.ts.
const genQuestions = (type: string, area: string): string[] => {
  const t = (type || "").trim() || "business";
  const a = (area || "").trim();
  const inArea = a ? ` in ${a}` : "";
  return [
    `What are the best ${t}${inArea}? Please give a ranked list of specific providers with a sentence on each.`,
    `I'm looking for a ${t}${inArea}. Who would you recommend, and why?`,
    `Who are the top-rated ${t}${inArea} right now? List them in order.`,
  ];
};

export function LlmVisibilityScanner(props: Props) {
  const key = props.siteUrl ? siteKey(props.siteUrl) : "";
  const notReady = !key;

  const [businessName, setBusinessName] = useState(props.siteBrand ?? "");
  const [businessLoc, setBusinessLoc] = useState<Loc>(null);
  const [businessType, setBusinessType] = useState("");
  const [area, setArea] = useState("");
  const [knowledgeMode, setKnowledgeMode] = useState<LlmVisKnowledgeMode>("training");
  const [competitors, setCompetitors] = useState<CompRow[]>([{ ...EMPTY_COMP }]);
  const [compLoc, setCompLoc] = useState<Loc[]>([null]); // parallel to competitors — the DataForSEO city pick
  const [country, setCountry] = useState(DEFAULT_COUNTRY); // filters the city typeahead (all entities)
  const [countries, setCountries] = useState<RankCountry[]>([]);
  const [questions, setQuestions] = useState<string[]>(genQuestions("", "")); // visibility-matrix questions
  const [questionsCustom, setQuestionsCustom] = useState(false); // true once the user edits them (stop auto-syncing to type/area)

  const [scans, setScans] = useState<LlmVisScanMeta[]>([]);
  const [result, setResult] = useState<LlmVisScanResult | null>(null);
  const [selectedScan, setSelectedScan] = useState<string>("latest");
  const [loaded, setLoaded] = useState(false);
  const [loading, setLoading] = useState(false);
  const [running, setRunning] = useState(false);
  const [status, setStatus] = useState<{ tone: "info" | "err"; text: string } | null>(null);
  const [balance, setBalance] = useState<DfsBalance | null>(null);

  const applyConfig = useCallback((c: LlmVisConfig | null) => {
    if (!c) return;
    setBusinessName(c.businessName || props.siteBrand || "");
    setBusinessLoc(c.businessLocation ? { name: c.businessLocation, code: 0 } : null);
    setBusinessType(c.businessType || "");
    setArea(c.area || "");
    setKnowledgeMode(c.knowledgeMode || "training");
    const comps = c.competitors.length ? c.competitors : [];
    setCompetitors(comps.length ? comps.map(x => ({ name: x.name, website: x.website })) : [{ ...EMPTY_COMP }]);
    setCompLoc(comps.length ? comps.map(x => x.location ? { name: x.location, code: 0 } : null) : [null]);
    // Default the country filter from a stored location so the typeahead is scoped sensibly.
    const inferred = countryOf(c.businessLocation) || countryOf(c.competitors.find(x => x.location)?.location ?? "");
    if (inferred) setCountry(inferred);
    // Questions: stored (custom) if present, else auto-generated from type/area.
    if (c.questions && c.questions.length) { setQuestions(c.questions); setQuestionsCustom(true); }
    else { setQuestionsCustom(false); setQuestions(genQuestions(c.businessType, c.area)); }
  }, [props.siteBrand]);

  const load = useCallback(async () => {
    if (!key) return;
    setLoading(true);
    try {
      const d = await getLlmVisibility(key);
      applyConfig(d.config);
      setScans(d.scans); setResult(d.latest); setSelectedScan("latest"); setLoaded(true);
    } catch (e) { setStatus({ tone: "err", text: e instanceof Error ? e.message : "Could not load." }); }
    finally { setLoading(false); }
  }, [key, applyConfig]);

  useEffect(() => { setResult(null); setScans([]); setLoaded(false); setStatus(null); }, [key]);
  useEffect(() => { void load(); }, [load, props.refreshToken]);
  useEffect(() => { (async () => { try { setBalance(await dfsBalance()); } catch { /* non-fatal */ } })(); }, []);
  // Country library from DataForSEO (strict) — scopes the city typeahead below.
  useEffect(() => { let alive = true; (async () => { try { const c = await rankCountries(); if (alive) setCountries(c); } catch { /* fall back to typed default */ } })(); return () => { alive = false; }; }, []);
  // Keep the default questions in sync with type/area until the user customizes them.
  useEffect(() => { if (!questionsCustom) setQuestions(genQuestions(businessType, area)); }, [businessType, area, questionsCustom]);

  // Persist the current config (best-effort). Locations come from the DataForSEO city picks.
  const persist = useCallback(async () => {
    if (!key) return;
    try {
      await updateLlmVisConfig(key, {
        businessName: businessName.trim(), businessLocation: businessLoc?.name ?? "",
        businessType: businessType.trim(), area: area.trim(), knowledgeMode,
        competitors: competitors.map((c, i) => ({ name: c.name.trim(), location: compLoc[i]?.name ?? "", website: c.website.trim() })).filter(c => c.name),
        // Empty = "use type/area defaults" (server regenerates); non-empty = the user's custom questions.
        questions: questionsCustom ? questions.map(q => q.trim()).filter(Boolean) : [],
      });
    } catch { /* soft-fail; a scan re-persists */ }
  }, [key, businessName, businessLoc, businessType, area, knowledgeMode, competitors, compLoc, questions, questionsCustom]);

  const setQ = (i: number, v: string) => { setQuestions(prev => prev.map((q, j) => j === i ? v : q)); setQuestionsCustom(true); };
  const addQ = () => { if (questions.length >= 8) return; setQuestions(prev => [...prev, ""]); setQuestionsCustom(true); };
  const removeQ = (i: number) => { setQuestions(prev => { const n = prev.filter((_, j) => j !== i); return n.length ? n : [""]; }); setQuestionsCustom(true); };
  const resetQ = () => { setQuestionsCustom(false); setQuestions(genQuestions(businessType, area)); setTimeout(() => void persist(), 0); };

  const setComp = (i: number, patch: Partial<CompRow>) => setCompetitors(prev => prev.map((c, j) => j === i ? { ...c, ...patch } : c));
  const setCompLocAt = (i: number, loc: Loc) => setCompLoc(prev => prev.map((l, j) => j === i ? loc : l));
  const addComp = () => { if (competitors.length >= 5) return; setCompetitors(prev => [...prev, { ...EMPTY_COMP }]); setCompLoc(prev => [...prev, null]); };
  const removeComp = (i: number) => {
    setCompetitors(prev => { const n = prev.filter((_, j) => j !== i); return n.length ? n : [{ ...EMPTY_COMP }]; });
    setCompLoc(prev => { const n = prev.filter((_, j) => j !== i); return n.length ? n : [null]; });
  };

  const runScan = useCallback(async () => {
    if (running || !key) return;
    if (!businessName.trim()) { void showAlert("Enter your business name first.", { title: "Business Required" }); return; }
    if (!businessLoc?.name) { void showAlert("Pick your business location from the city list.", { title: "Location Required" }); return; }
    await persist();
    setRunning(true);
    setStatus({ tone: "info", text: "Scanning the AI models — this can take a few minutes…" });
    let p = visRuns.get(key);
    if (!p) { p = runLlmVisibilityScan(key).then(r => ({ result: r.result, scans: r.scans })); visRuns.set(key, p); void p.catch(() => {}).finally(() => visRuns.delete(key)); }
    try {
      const d = await p;
      setResult(d.result); setScans(d.scans); setSelectedScan("latest");
      const errs = d.result.errors.length;
      setStatus({ tone: errs ? "err" : "info", text: errs ? `Scan complete with ${errs} model error(s) — partial results shown.` : "Scan complete." });
    } catch (e) { setStatus({ tone: "err", text: e instanceof Error ? e.message : "Scan failed." }); }
    finally { setRunning(false); }
  }, [running, key, businessName, businessLoc, persist]);

  // Re-attach to an in-flight scan after a tab switch.
  useEffect(() => {
    if (!key) return;
    const p = visRuns.get(key);
    if (!p) return;
    let alive = true;
    setRunning(true);
    setStatus({ tone: "info", text: "Scanning the AI models — this can take a few minutes…" });
    p.then(d => { if (alive) { setResult(d.result); setScans(d.scans); setSelectedScan("latest"); setStatus({ tone: "info", text: "Scan complete." }); } })
      .catch(e => { if (alive) setStatus({ tone: "err", text: e instanceof Error ? e.message : "Scan failed." }); })
      .finally(() => { if (alive) setRunning(false); });
    return () => { alive = false; };
  }, [key]);

  const onSelectScan = useCallback(async (id: string) => {
    setSelectedScan(id);
    if (id === "latest") { void load(); return; }
    setLoading(true);
    try { const r = await getLlmVisScan(id); if (r) setResult(r); }
    catch (e) { setStatus({ tone: "err", text: e instanceof Error ? e.message : "Could not load scan." }); }
    finally { setLoading(false); }
  }, [load]);

  // ── AI-Crawlability & Technical Check (free, deterministic — no credits) ──
  const [crawlChecks, setCrawlChecks] = useState<CrawlabilityCheckMeta[]>([]);
  const [crawlResult, setCrawlResult] = useState<CrawlabilityResult | null>(null);
  const [crawlSelected, setCrawlSelected] = useState<string>("latest");
  const [crawlCheckId, setCrawlCheckId] = useState<string | null>(null);
  const [crawlRunning, setCrawlRunning] = useState(false);
  const [crawlStatus, setCrawlStatus] = useState<{ tone: "info" | "err"; text: string } | null>(null);
  const [explaining, setExplaining] = useState(false);
  const [explainErr, setExplainErr] = useState<string | null>(null);

  const loadCrawl = useCallback(async () => {
    if (!key) return;
    try { const d = await getCrawlability(key); setCrawlChecks(d.checks); setCrawlResult(d.latest); setCrawlCheckId(d.latestId); setCrawlSelected("latest"); }
    catch { /* non-fatal */ }
  }, [key]);

  useEffect(() => { setCrawlResult(null); setCrawlChecks([]); setCrawlCheckId(null); setCrawlStatus(null); setExplainErr(null); }, [key]);
  useEffect(() => { void loadCrawl(); }, [loadCrawl, props.refreshToken]);

  const runCrawl = useCallback(async () => {
    if (crawlRunning || !key || !props.siteUrl) return;
    setCrawlRunning(true); setExplainErr(null);
    setCrawlStatus({ tone: "info", text: "Fetching robots.txt, homepage, sitemap & probing AI-bot access…" });
    let p = crawlRuns.get(key);
    if (!p) { p = runCrawlabilityCheck(key, props.siteUrl).then(r => ({ result: r.result, checkId: r.checkId, checks: r.checks })); crawlRuns.set(key, p); void p.catch(() => {}).finally(() => crawlRuns.delete(key)); }
    try {
      const d = await p;
      setCrawlResult(d.result); setCrawlChecks(d.checks); setCrawlCheckId(d.checkId); setCrawlSelected("latest");
      const s = d.result.summary;
      setCrawlStatus({ tone: s.critical || s.warning ? "err" : "info", text: `Done — ${s.critical} critical, ${s.warning} warning, ${s.ok} passing.` });
    } catch (e) { setCrawlStatus({ tone: "err", text: e instanceof Error ? e.message : "Technical check failed." }); }
    finally { setCrawlRunning(false); }
  }, [crawlRunning, key, props.siteUrl]);

  // Re-attach to an in-flight technical check after a tab switch.
  useEffect(() => {
    if (!key) return;
    const p = crawlRuns.get(key);
    if (!p) return;
    let alive = true; setCrawlRunning(true);
    setCrawlStatus({ tone: "info", text: "Running the technical check…" });
    p.then(d => { if (alive) { setCrawlResult(d.result); setCrawlChecks(d.checks); setCrawlCheckId(d.checkId); setCrawlSelected("latest"); setCrawlStatus({ tone: "info", text: "Technical check complete." }); } })
      .catch(e => { if (alive) setCrawlStatus({ tone: "err", text: e instanceof Error ? e.message : "Technical check failed." }); })
      .finally(() => { if (alive) setCrawlRunning(false); });
    return () => { alive = false; };
  }, [key]);

  const onSelectCrawl = useCallback(async (id: string) => {
    setCrawlSelected(id); setExplainErr(null);
    if (id === "latest") { void loadCrawl(); return; }
    try { const r = await getCrawlabilityCheck(id); if (r) { setCrawlResult(r); setCrawlCheckId(id); } }
    catch (e) { setCrawlStatus({ tone: "err", text: e instanceof Error ? e.message : "Could not load check." }); }
  }, [loadCrawl]);

  const runExplain = useCallback(async () => {
    if (!crawlCheckId || explaining) return;
    setExplaining(true); setExplainErr(null);
    try { const text = await explainCrawlability(crawlCheckId); setCrawlResult(prev => prev ? { ...prev, aiExplanation: text } : prev); }
    catch (e) { setExplainErr(e instanceof Error ? e.message : "Could not generate an explanation."); }
    finally { setExplaining(false); }
  }, [crawlCheckId, explaining]);

  // ── Theme ──
  const isDark = typeof document !== "undefined" && document.documentElement.getAttribute("data-theme") === "dark";
  const cardBg = isDark ? "#16223a" : "#fff";
  const cardBorder = isDark ? "rgba(255,255,255,.14)" : "#e2e8f0";
  const hdrBg = isDark ? "#26375a" : "#eef2ff";
  const boxBg = isDark ? "rgba(255,255,255,.04)" : "#f8fafc";
  const boxBorder = isDark ? "rgba(255,255,255,.12)" : "#e2e8f0";
  const ink = isDark ? "#e6edf6" : "#0f172a";
  const ink2 = isDark ? "#93a4c0" : "#64748b";
  const inkMuted = isDark ? "#7c8aa0" : "#94a3b8";
  const inkMid = isDark ? "#cbd5e1" : "#334155";
  const link = isDark ? "#7dd3fc" : "#2563eb";
  const red = isDark ? "#f87171" : "#dc2626";
  const green = isDark ? "#86efac" : "#16a34a";
  const accent = "#f97316";
  const chipBg = isDark ? "rgba(255,255,255,.06)" : "#f1f5f9";
  const inpT: React.CSSProperties = { ...inp, border: `1px solid ${isDark ? "rgba(255,255,255,.18)" : "#cbd5e1"}`, background: isDark ? "#1a2740" : "#fff", color: ink };
  const lbl: React.CSSProperties = { display: "block", fontSize: 12, fontWeight: 700, textTransform: "uppercase", letterSpacing: .5, color: ink2, marginBottom: 3 };
  const colLbl: React.CSSProperties = { fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: .4, color: inkMuted };
  const help: React.CSSProperties = { fontFamily: "'Inter','Mulish',sans-serif", fontSize: 12, color: ink2, marginTop: 4, lineHeight: 1.5, maxWidth: 820 };
  const brandTitle: React.CSSProperties = { fontFamily: "'Oswald','Barlow Condensed','Inter',sans-serif", fontSize: 20, fontWeight: 500, textTransform: "uppercase", color: accent, letterSpacing: ".7px" };
  const bannerBase: React.CSSProperties = { margin: "0 9px 16px", padding: "10px 14px", borderRadius: 8, border: "1px solid", fontSize: 12.5, lineHeight: 1.5, fontFamily: "'Inter','Mulish',sans-serif" };
  const sentColor = (s: string) => /pos/i.test(s) ? green : /neg/i.test(s) ? red : /mix/i.test(s) ? (isDark ? "#fcd34d" : "#b45309") : inkMuted;
  const priColor = (p: string) => /high/i.test(p) ? red : /low/i.test(p) ? inkMuted : (isDark ? "#fcd34d" : "#b45309");
  const amber = isDark ? "#fcd34d" : "#b45309";
  const sevColor = (s: string) => s === "critical" ? red : s === "warning" ? amber : s === "ok" ? green : inkMuted;
  const sevBg = (s: string) => s === "critical" ? (isDark ? "rgba(239,68,68,.14)" : "#fef2f2") : s === "warning" ? (isDark ? "rgba(245,158,11,.14)" : "#fffbeb") : s === "ok" ? (isDark ? "rgba(34,197,94,.14)" : "#f0fdf4") : (isDark ? "rgba(255,255,255,.06)" : "#f1f5f9");
  const sevLabel: Record<string, string> = { critical: "Critical", warning: "Warning", ok: "Pass", info: "Info" };

  const countryNames = countries.length ? countries.map(c => c.name) : [DEFAULT_COUNTRY];
  const entityName = (k: string) => result?.entities.find(e => e.key === k)?.name ?? k;
  const chip = (t: string, i: number) => <span key={i} style={{ fontSize: 10.5, color: inkMid, background: chipBg, border: `1px solid ${boxBorder}`, borderRadius: 999, padding: "2px 8px", maxWidth: "100%", overflowWrap: "anywhere", wordBreak: "break-word", whiteSpace: "normal" }}>{t}</span>;

  // wrap: shared word-wrapping so long words/URLs/lists never spill past the card; the card grows
  // vertically to fit instead of overflowing its box.
  const wrap: React.CSSProperties = { overflowWrap: "anywhere", wordBreak: "break-word", minWidth: 0 };
  const renderCard = (c: EntityKnowledgeCard) => (
    <div style={{ border: `1px solid ${cardBorder}`, borderRadius: 8, background: cardBg, padding: "10px 12px", opacity: c.hasKnowledge ? 1 : 0.72, ...wrap }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8, marginBottom: 5 }}>
        <span style={{ fontSize: 12, fontWeight: 800, color: ink, ...wrap }}>{platLabel(c.platform)}</span>
        <span style={{ fontSize: 9.5, fontWeight: 700, textTransform: "uppercase", letterSpacing: .4, color: c.hasKnowledge ? green : inkMuted, border: `1px solid ${boxBorder}`, borderRadius: 4, padding: "1px 6px", flexShrink: 0, whiteSpace: "nowrap" }}>{c.hasKnowledge ? `knows · ${c.confidence}` : "no knowledge"}</span>
      </div>
      {c.hasKnowledge ? (
        <>
          <div style={{ fontSize: 12, color: inkMid, lineHeight: 1.5, ...wrap }}>{c.summary || c.identity || "—"}</div>
          {c.knownFor.length > 0 && <div style={{ marginTop: 6 }}><div style={{ fontSize: 10, fontWeight: 700, textTransform: "uppercase", color: inkMuted, marginBottom: 3 }}>Known for</div><div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>{c.knownFor.map(chip)}</div></div>}
          {c.strengths.length > 0 && <div style={{ marginTop: 6 }}><div style={{ fontSize: 10, fontWeight: 700, textTransform: "uppercase", color: inkMuted, marginBottom: 3 }}>Strengths the model cites</div><div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>{c.strengths.map(chip)}</div></div>}
          <div style={{ marginTop: 6, display: "flex", flexWrap: "wrap", gap: 10, fontSize: 11, color: ink2 }}>
            <span style={wrap}>Sentiment: <strong style={{ color: sentColor(c.sentiment) }}>{c.sentiment}</strong></span>
            {c.associated.length > 0 && <span style={wrap}>Links to: <span style={{ color: inkMid }}>{c.associated.slice(0, 4).join(", ")}</span></span>}
          </div>
          {c.disambiguationRisk && <div style={{ marginTop: 6, fontSize: 11, color: isDark ? "#fcd34d" : "#b45309", ...wrap }}>⚠ {c.disambiguationRisk}</div>}
          {c.uncertainties.length > 0 && <div style={{ marginTop: 4, fontSize: 10.5, color: inkMuted, ...wrap }}>Uncertain: {c.uncertainties.slice(0, 3).join("; ")}</div>}
          {c.sources.length > 0 && <div style={{ marginTop: 6, fontSize: 10.5, ...wrap }}><span style={{ color: inkMuted }}>Sources: </span>{c.sources.slice(0, 5).map((u, i) => <a key={i} href={u.startsWith("http") ? u : `https://${u}`} target="_blank" rel="noopener noreferrer" style={{ color: link, marginRight: 6, overflowWrap: "anywhere", wordBreak: "break-all" }}>{domainOf(u)}</a>)}</div>}
        </>
      ) : <div style={{ fontSize: 11.5, color: inkMuted, ...wrap }}>{c.error || "This model reported no specific knowledge of this entity."}</div>}
    </div>
  );

  return (
    <div className="content-drawer" style={{ marginTop: 8 }}>
      {/* DataForSEO balance warnings sit above the title so they're seen first. */}
      {balance?.configured && balance.ok === false && <div style={{ ...bannerBase, background: isDark ? "rgba(239,68,68,.12)" : "#fef2f2", borderColor: isDark ? "rgba(239,68,68,.4)" : "#fecaca", color: isDark ? "#fca5a5" : "#991b1b" }}><strong>DataForSEO account issue:</strong> {balance.error || "account unreachable"}.</div>}
      {balance?.configured && balance.ok && balance.balanceUsd != null && balance.balanceUsd < LOW_BALANCE_USD && <div style={{ ...bannerBase, background: isDark ? "rgba(245,158,11,.12)" : "#fffbeb", borderColor: isDark ? "rgba(245,158,11,.4)" : "#fcd34d", color: isDark ? "#fcd34d" : "#92400e" }}><strong>Low DataForSEO balance: {usd(balance.balanceUsd)}.</strong> Top up at <a href="https://app.dataforseo.com/" target="_blank" rel="noopener noreferrer" style={{ color: "inherit", fontWeight: 700 }}>dataforseo.com</a>.</div>}

      {/* ── Technical / AI Crawlability (free — no credits) — shown first; independent of the paid scan ── */}
      <div style={{ padding: "0 9px", marginBottom: 24 }}>
        <div className="cd-section">
          <div className="cd-section-hd">
            <span className="cd-hd-brand">Technical / AI Crawlability</span>
          </div>
          <p className="cd-help">
            Checks whether the AI models&rsquo; crawlers can actually reach and read your site — <strong>robots.txt</strong> AI-bot
            rules, <strong>WAF/CDN bot-blocking</strong> (we fetch your homepage as a browser and as each AI bot and compare),
            whether the page is <strong>server-rendered</strong> or a JavaScript-only SPA that non-JS crawlers can&rsquo;t read,
            plus <strong>structured data</strong>, <strong>XML sitemap</strong> and <strong>llms.txt</strong>. Every finding and fix
            uses your site&rsquo;s real values. Runs in seconds and uses no credits.
          </p>

          <div style={{ display: "flex", alignItems: "center", gap: 12, marginTop: 10, marginBottom: 4, flexWrap: "wrap" }}>
            <button onClick={() => void runCrawl()} disabled={crawlRunning || notReady}
              style={{ padding: "9px 16px", border: "none", borderRadius: 0, fontFamily: "'Inter','Mulish',sans-serif", fontSize: 13, fontWeight: 700, color: "#fff", cursor: crawlRunning || notReady ? "default" : "pointer", background: crawlRunning || notReady ? "#fdba74" : "linear-gradient(135deg,#ea580c,#f97316)", whiteSpace: "nowrap" }}>
              {crawlRunning ? "Checking…" : "Run technical check"}
            </button>
            {crawlRunning && <span style={{ display: "inline-flex", alignItems: "center", gap: 7, fontSize: 12.5, color: ink2 }}><span className="lsg-spinner" />{crawlStatus?.text}</span>}
            {notReady && <span style={{ fontSize: 12.5, fontWeight: 600, color: link }}>Select a website to use this tool.</span>}
            {!crawlRunning && crawlChecks.length > 0 && (
              <select value={crawlSelected} onChange={e => void onSelectCrawl(e.target.value)} style={{ ...inpT, width: "auto", padding: "5px 9px", cursor: "pointer", marginLeft: "auto" }}>
                <option value="latest">Latest check</option>
                {crawlChecks.map(c => <option key={c.id} value={c.id}>{fmtDateTime(c.capturedAt)}</option>)}
              </select>
            )}
          </div>
          {crawlStatus && !crawlRunning && <div style={{ fontSize: 12.5, color: crawlStatus.tone === "err" ? red : ink2, marginBottom: 4 }}>{crawlStatus.text}</div>}

          {crawlResult && (
            <div style={{ marginTop: 12 }}>
              {/* Summary + meta */}
              <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", marginBottom: 12 }}>
                {(["critical", "warning", "ok", "info"] as const).map(s => crawlResult.summary[s] > 0 && (
                  <span key={s} style={{ fontSize: 11.5, fontWeight: 700, color: sevColor(s), background: sevBg(s), border: `1px solid ${boxBorder}`, borderRadius: 999, padding: "3px 11px" }}>
                    {crawlResult.summary[s]} {s === "ok" ? "passing" : s}
                  </span>
                ))}
                <span style={{ fontSize: 11.5, color: inkMuted, marginLeft: "auto" }}>
                  Checked {fmtDateTime(crawlResult.capturedAt)} · rendering: <strong style={{ color: crawlResult.rendering.renderMode === "js-only" ? red : crawlResult.rendering.renderMode === "hybrid" ? amber : green }}>{RENDER_LABEL[crawlResult.rendering.renderMode]}</strong>
                </span>
              </div>

              {/* Findings */}
              <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                {crawlResult.findings.map(f => (
                  <div key={f.id} style={{ border: `1px solid ${cardBorder}`, borderLeft: `4px solid ${sevColor(f.severity)}`, borderRadius: 8, background: cardBg, padding: "10px 14px", ...wrap }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8 }}>
                      <span style={{ fontSize: 13, fontWeight: 700, color: ink, ...wrap }}>{f.title}</span>
                      <span style={{ fontSize: 9.5, fontWeight: 700, textTransform: "uppercase", letterSpacing: .4, color: sevColor(f.severity), background: sevBg(f.severity), borderRadius: 4, padding: "2px 7px", flexShrink: 0, whiteSpace: "nowrap" }}>{sevLabel[f.severity] ?? f.severity}</span>
                    </div>
                    <div style={{ fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: .4, color: inkMuted, marginTop: 3 }}>{f.area}</div>
                    <div style={{ fontSize: 12, color: ink2, marginTop: 5, lineHeight: 1.5, ...wrap }}>{f.detail}</div>
                    {f.evidence && <div style={{ fontSize: 11, color: inkMuted, marginTop: 5, fontFamily: MONO, ...wrap }}>{f.evidence}</div>}
                    {f.severity !== "ok" && f.fix && (
                      <details style={{ marginTop: 7 }}>
                        <summary style={{ cursor: "pointer", fontSize: 11.5, fontWeight: 700, color: link }}>How to fix</summary>
                        <div style={{ fontSize: 12, color: inkMid, marginTop: 5, lineHeight: 1.55, whiteSpace: "pre-wrap", fontFamily: /User-agent|Sitemap:/.test(f.fix) ? MONO : "'Inter','Mulish',sans-serif", ...wrap }}>{f.fix}</div>
                      </details>
                    )}
                  </div>
                ))}
              </div>

              {/* robots.txt AI-bot table */}
              <details style={{ marginTop: 14 }}>
                <summary style={{ cursor: "pointer", fontSize: 12, fontWeight: 700, color: inkMid }}>robots.txt — per AI-crawler access ({crawlResult.robots.present ? `${crawlResult.robots.bots.filter(b => b.status !== "disallowed").length}/${crawlResult.robots.bots.length} allowed` : "no robots.txt"})</summary>
                <div style={{ overflowX: "auto", marginTop: 8 }}>
                  <table className="cd-history" style={{ margin: 0, width: "100%" }}>
                    <thead><tr><th style={{ textAlign: "left" }}>AI crawler</th><th style={{ textAlign: "left" }}>Vendor</th><th style={{ textAlign: "left" }}>Access</th><th style={{ textAlign: "left" }}>Matched rule</th></tr></thead>
                    <tbody>
                      {crawlResult.robots.bots.map(b => (
                        <tr key={b.token}>
                          <td style={{ fontSize: 11.5, color: ink, fontWeight: b.major ? 700 : 500 }}>{b.token}<div style={{ fontSize: 10, color: inkMuted, fontWeight: 400 }}>{b.purpose}</div></td>
                          <td style={{ fontSize: 11.5, color: ink2 }}>{b.vendor}</td>
                          <td style={{ fontSize: 11.5, color: b.status === "disallowed" ? red : b.status === "allowed" ? green : inkMuted, fontWeight: 700 }}>{b.status === "disallowed" ? "Blocked" : b.status === "allowed" ? "Allowed" : "No rule"}</td>
                          <td style={{ fontSize: 11, color: inkMuted, fontFamily: MONO, ...wrap }}>{b.directive ? `${b.directive}${b.line ? ` (line ${b.line})` : ""}` : "—"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </details>

              {/* Dual-UA WAF probe table */}
              <details style={{ marginTop: 10 }}>
                <summary style={{ cursor: "pointer", fontSize: 12, fontWeight: 700, color: inkMid }}>Live bot-access probe — browser vs. AI-bot user-agent{crawlResult.botAccess.vendorHint ? ` · ${crawlResult.botAccess.vendorHint}` : ""}</summary>
                <div style={{ overflowX: "auto", marginTop: 8 }}>
                  <table className="cd-history" style={{ margin: 0, width: "100%" }}>
                    <thead><tr><th style={{ textAlign: "left" }}>Crawler UA</th><th style={{ textAlign: "left" }}>Browser</th><th style={{ textAlign: "left" }}>As bot</th><th style={{ textAlign: "left" }}>Result</th></tr></thead>
                    <tbody>
                      {crawlResult.botAccess.probes.map(p => (
                        <tr key={p.token}>
                          <td style={{ fontSize: 11.5, color: ink, fontWeight: 600 }}>{p.token}</td>
                          <td style={{ fontSize: 11.5, color: ink2 }}>{p.normalStatus || "—"}</td>
                          <td style={{ fontSize: 11.5, color: p.blocked ? red : ink2, fontWeight: p.blocked ? 700 : 400 }}>{p.botStatus || (p.note.includes("fail") ? "fail" : "—")}</td>
                          <td style={{ fontSize: 11.5, color: p.blocked ? red : green, fontWeight: 700 }}>{p.blocked ? "Blocked" : "OK"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <p style={{ fontSize: 10.5, color: inkMuted, margin: "6px 0 0" }}>Tests the user-agent layer only — some WAFs also fingerprint IP/TLS, which this can&rsquo;t see.</p>
              </details>

              {/* Explain with AI */}
              <div style={{ marginTop: 16, borderTop: `1px solid ${boxBorder}`, paddingTop: 12 }}>
                {crawlResult.aiExplanation ? (
                  <div style={{ border: `1px solid ${cardBorder}`, borderLeft: `4px solid ${accent}`, borderRadius: 8, background: cardBg, padding: "12px 14px" }}>
                    <div style={{ fontSize: 11.5, fontWeight: 700, textTransform: "uppercase", letterSpacing: .5, color: accent, marginBottom: 6 }}>AI briefing</div>
                    <div style={{ fontSize: 12.5, color: inkMid, lineHeight: 1.6, whiteSpace: "pre-wrap", ...wrap }}>{crawlResult.aiExplanation}</div>
                  </div>
                ) : (
                  <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
                    <button onClick={() => void runExplain()} disabled={explaining || !crawlCheckId}
                      style={{ padding: "7px 14px", border: `1px solid ${accent}`, borderRadius: 6, background: "none", color: accent, fontSize: 12, fontWeight: 700, cursor: explaining || !crawlCheckId ? "default" : "pointer", fontFamily: "'Inter','Mulish',sans-serif", opacity: explaining || !crawlCheckId ? 0.6 : 1 }}>
                      {explaining ? "Asking Claude…" : "Explain with AI"}
                    </button>
                    <span style={{ fontSize: 11, color: inkMuted }}>Optional — the only part of this check that uses an AI (Anthropic) credit.</span>
                  </div>
                )}
                {explainErr && <div style={{ fontSize: 12, color: red, marginTop: 8 }}>{explainErr}</div>}
              </div>
            </div>
          )}
        </div>
      </div>

      <div style={{ padding: "0 9px", marginBottom: 16 }}>
        <div className="cd-hd-brand" style={brandTitle}>LLM Visibility Scanner</div>
        <div className="cd-help" style={help}>
          See what each AI model knows about your business and up to <strong>5 competitors</strong> as entities, who the models
          <strong> recommend</strong> when asked for the best {businessType.trim() || "businesses"} in your area, and how to be
          mentioned more often than those competitors. Knowledge can be pulled from each model&rsquo;s <strong>training data
          only</strong> or with <strong>live search</strong> allowed. A scan queries each model for each entity and provides an
          analysis, so it&rsquo;s run on demand and its results are saved. Model answers vary run to run — treat a scan as a
          snapshot and watch the trend across scans.
        </div>

        {/* ── Config ── */}
        <div style={{ border: `1px solid ${boxBorder}`, borderRadius: 8, background: boxBg, padding: "12px 14px", marginTop: 12 }}>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 12, alignItems: "flex-end" }}>
            <label style={{ flex: "1 1 190px", minWidth: 160 }}><span style={lbl}>Your business name</span>
              <input value={businessName} disabled={notReady} onChange={e => setBusinessName(e.target.value)} onBlur={() => void persist()} placeholder="Your business name" style={inpT} /></label>
            <label style={{ flex: "0 0 auto", minWidth: 150 }}><span style={lbl}>Country</span>
              <select value={country} disabled={notReady} onChange={e => setCountry(e.target.value)} style={{ ...inpT, cursor: "pointer" }}>
                {countryNames.includes(country) ? null : <option value={country}>{country}</option>}
                {countryNames.map(n => <option key={n} value={n}>{n}</option>)}
              </select></label>
            <label style={{ flex: "1 1 200px", minWidth: 180 }}><span style={lbl}>Your location</span>
              <CityPicker country={country} value={businessLoc} onChange={v => { setBusinessLoc(v); setTimeout(() => void persist(), 0); }} isDark={isDark} disabled={notReady} inpStyle={inpT} /></label>
            <label style={{ flex: "1 1 170px", minWidth: 150 }}><span style={lbl}>Business type</span>
              <input value={businessType} disabled={notReady} onChange={e => setBusinessType(e.target.value)} onBlur={() => void persist()} placeholder="e.g. teen rehab center" style={inpT} /></label>
            <label style={{ flex: "1 1 150px", minWidth: 140 }}><span style={lbl}>Recommendation area</span>
              <input value={area} disabled={notReady} onChange={e => setArea(e.target.value)} onBlur={() => void persist()} placeholder="defaults to your location" style={inpT} /></label>
          </div>

          {/* Knowledge-source toggle */}
          <div style={{ marginTop: 12 }}>
            <span style={lbl}>Knowledge source</span>
            <div style={{ display: "inline-flex", border: `1px solid ${isDark ? "rgba(255,255,255,.18)" : "#cbd5e1"}`, borderRadius: 6, overflow: "hidden" }}>
              {([["training", "Training data only"], ["live", "Allow live search"]] as [LlmVisKnowledgeMode, string][]).map(([m, label]) => (
                <button key={m} disabled={notReady} onClick={() => { setKnowledgeMode(m); setTimeout(() => void persist(), 0); }}
                  style={{ padding: "7px 14px", border: "none", cursor: notReady ? "default" : "pointer", fontSize: 12, fontWeight: 700, fontFamily: "'Inter','Mulish',sans-serif", background: knowledgeMode === m ? "linear-gradient(135deg,#ea580c,#f97316)" : (isDark ? "#1a2740" : "#fff"), color: knowledgeMode === m ? "#fff" : inkMid }}>
                  {label}
                </button>
              ))}
            </div>
            <span style={{ fontSize: 11, color: inkMuted, marginLeft: 10 }}>{knowledgeMode === "training" ? "Probes each model's training knowledge (no web search). Perplexity is excluded — it always searches." : "Lets the models search the web to answer, incl. Perplexity."}</span>
          </div>

          {/* Competitors */}
          <div style={{ marginTop: 14 }}>
            <span style={lbl}>Competitors (up to 5) <span style={{ textTransform: "none", fontWeight: 400, color: inkMuted, letterSpacing: 0 }}>— Optional</span></span>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 8, alignItems: "center", marginTop: 4, marginBottom: 4 }}>
              <span style={{ ...colLbl, flex: "2 1 200px", minWidth: 160 }}>Full Business Name</span>
              <span style={{ ...colLbl, flex: "1 1 160px", minWidth: 140 }}>City</span>
              <span style={{ ...colLbl, flex: "1 1 150px", minWidth: 130 }}>Website</span>
              <span style={{ width: 23, flexShrink: 0 }} />
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              {competitors.map((c, i) => (
                <div key={i} style={{ display: "flex", flexWrap: "wrap", gap: 8, alignItems: "center" }}>
                  <input value={c.name} disabled={notReady} onChange={e => setComp(i, { name: e.target.value })} onBlur={() => void persist()} placeholder={`Competitor ${i + 1} full business name`} style={{ ...inpT, flex: "2 1 200px", minWidth: 160 }} />
                  <div style={{ flex: "1 1 160px", minWidth: 140 }}>
                    <CityPicker country={country} value={compLoc[i] ?? null} onChange={v => { setCompLocAt(i, v); setTimeout(() => void persist(), 0); }} isDark={isDark} disabled={notReady} inpStyle={inpT} />
                  </div>
                  <input value={c.website} disabled={notReady} onChange={e => setComp(i, { website: e.target.value })} onBlur={() => void persist()} placeholder="website (optional)" style={{ ...inpT, flex: "1 1 150px", minWidth: 130 }} />
                  <button onClick={() => { removeComp(i); setTimeout(() => void persist(), 0); }} title="Remove competitor" style={{ background: "none", border: "none", cursor: "pointer", color: red, padding: 4, lineHeight: 0 }}>
                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6" /><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" /><path d="M10 11v6" /><path d="M14 11v6" /></svg>
                  </button>
                </div>
              ))}
            </div>
            {competitors.length < 5 && <button onClick={addComp} style={{ marginTop: 8, padding: "6px 12px", border: `1px dashed ${cardBorder}`, borderRadius: 6, background: "none", color: link, fontSize: 12, fontWeight: 700, cursor: "pointer", fontFamily: "'Inter','Mulish',sans-serif" }}>+ Add competitor</button>}
          </div>

          {/* Visibility-matrix questions — editable, and shown even before a scan so the user knows what will be asked */}
          <div style={{ marginTop: 14 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
              <span style={lbl}>Questions asked to the models (Visibility Matrix)</span>
              <button onClick={resetQ} disabled={notReady} style={{ background: "none", border: "none", color: link, fontSize: 11.5, fontWeight: 700, cursor: notReady ? "default" : "pointer", padding: 0 }}>Reset to defaults</button>
            </div>
            <p style={{ fontSize: 11, color: inkMuted, margin: "0 0 6px" }}>Sent to all 5 models (with web search) to see who they recommend — this builds the Visibility Matrix. Edit them, or keep the defaults built from your business type &amp; area.</p>
            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              {questions.map((q, i) => (
                <div key={i} style={{ display: "flex", gap: 8, alignItems: "center" }}>
                  <input value={q} disabled={notReady} onChange={e => setQ(i, e.target.value)} onBlur={() => void persist()} placeholder={`Question ${i + 1}`} style={{ ...inpT, flex: 1 }} />
                  {questions.length > 1 && (
                    <button onClick={() => { removeQ(i); setTimeout(() => void persist(), 0); }} title="Remove question" style={{ background: "none", border: "none", cursor: "pointer", color: red, padding: 4, lineHeight: 0 }}>
                      <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6" /><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" /><path d="M10 11v6" /><path d="M14 11v6" /></svg>
                    </button>
                  )}
                </div>
              ))}
            </div>
            {questions.length < 8 && <button onClick={addQ} disabled={notReady} style={{ marginTop: 8, padding: "6px 12px", border: `1px dashed ${cardBorder}`, borderRadius: 6, background: "none", color: link, fontSize: 12, fontWeight: 700, cursor: notReady ? "default" : "pointer", fontFamily: "'Inter','Mulish',sans-serif" }}>+ Add question</button>}
          </div>

          <div style={{ display: "flex", alignItems: "center", gap: 12, marginTop: 14, flexWrap: "wrap" }}>
            <button
              onClick={async () => {
                if (running || notReady) return;
                const ok = await showConfirm(
                  "This runs a full scan: every AI model is queried about your business and each competitor, then the results are analyzed. It can take a few minutes. Continue?",
                  { title: "Run Visibility Scan", confirmLabel: "Run Scan", cancelLabel: "Cancel", tone: "info" },
                );
                if (ok) void runScan();
              }}
              disabled={running || notReady}
              style={{ padding: "10px 18px", border: "none", borderRadius: 0, fontFamily: "'Inter','Mulish',sans-serif", fontSize: 13, fontWeight: 700, color: "#fff", cursor: running || notReady ? "default" : "pointer", background: running || notReady ? "#fdba74" : "linear-gradient(135deg,#ea580c,#f97316)", whiteSpace: "nowrap" }}
            >{running ? "Scanning…" : "Run Scan"}</button>
            {running && <span style={{ display: "inline-flex", alignItems: "center", gap: 7, fontSize: 12.5, color: ink2 }}><span className="lsg-spinner" />{status?.text}</span>}
            {notReady && <span style={{ fontSize: 12.5, fontWeight: 600, color: link }}>Select a website to use this tool.</span>}
          </div>
        </div>
      </div>

      {status && !running && <div style={{ padding: "0 9px", marginBottom: 16, fontSize: 12.5, color: status.tone === "err" ? red : ink2, display: "flex", alignItems: "center", gap: 7 }}>{loading && <span className="lsg-spinner" />}{status.text}</div>}

      {!result ? (
        <div style={{ padding: "0 9px" }}><p className="cd-help">{loading ? "Loading…" : loaded ? "No scan yet — fill in your business, add competitors, and click Run Scan." : ""}</p></div>
      ) : (
        <>
          {/* Scan meta + history */}
          <div style={{ padding: "0 9px", marginBottom: 16, display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 8 }}>
            <span style={{ fontSize: 11.5, color: inkMuted }}>Scanned {fmtDateTime(result.generatedAt)} · {result.knowledgeMode === "live" ? "live search" : "training data only"}{result.errors.length ? ` · ${result.errors.length} model error(s)` : ""}</span>
            {scans.length > 0 && (
              <select value={selectedScan} onChange={e => void onSelectScan(e.target.value)} style={{ ...inpT, width: "auto", padding: "5px 9px", cursor: "pointer" }}>
                <option value="latest">Latest scan</option>
                {scans.map(s => <option key={s.id} value={s.id}>{fmtDateTime(s.capturedAt)}</option>)}
              </select>
            )}
          </div>

          {result.errors.length > 0 && (
            <div style={{ padding: "0 9px", marginBottom: 16 }}>
              <details style={{ border: `1px solid ${isDark ? "rgba(239,68,68,.4)" : "#fecaca"}`, background: isDark ? "rgba(239,68,68,.1)" : "#fef2f2", borderRadius: 8, padding: "8px 12px" }}>
                <summary style={{ cursor: "pointer", fontSize: 12.5, fontWeight: 700, color: isDark ? "#fca5a5" : "#991b1b" }}>{result.errors.length} model error(s) during this scan — click to view</summary>
                <ul style={{ margin: "8px 0 0", paddingLeft: 18, display: "flex", flexDirection: "column", gap: 3 }}>
                  {result.errors.slice(0, 30).map((e, i) => <li key={i} style={{ fontSize: 11.5, color: isDark ? "#fca5a5" : "#991b1b", wordBreak: "break-word" }}>{e}</li>)}
                </ul>
              </details>
            </div>
          )}

          {/* Why competitors win + Share of Model */}
          <div style={{ padding: "0 9px", marginBottom: 24 }}>
            <div className="cd-section">
              <div className="cd-section-hd"><span className="cd-hd-brand">Share of Model &amp; Why Competitors Win</span></div>
              {result.whyCompetitorsWin && <p className="cd-help" style={{ color: inkMid }}>{result.whyCompetitorsWin}</p>}
              <div style={{ display: "flex", flexDirection: "column", gap: 6, marginTop: 8 }}>
                {[...result.shareOfModel].sort((a, b) => b.score - a.score).map(s => {
                  const e = result.entities.find(x => x.key === s.entityKey);
                  return (
                    <div key={s.entityKey} style={{ display: "flex", alignItems: "center", gap: 10 }}>
                      <span style={{ width: 180, flexShrink: 0, fontSize: 12, fontWeight: e?.isYou ? 800 : 600, color: e?.isYou ? accent : ink, whiteSpace: "normal", overflowWrap: "anywhere", wordBreak: "break-word", lineHeight: 1.3 }}>{e?.isYou ? "★ " : ""}{e?.name ?? s.entityKey}</span>
                      <div style={{ flex: 1, height: 16, background: boxBg, borderRadius: 4, overflow: "hidden", border: `1px solid ${boxBorder}` }}>
                        <div style={{ width: `${Math.max(2, s.score)}%`, height: "100%", background: e?.isYou ? "linear-gradient(90deg,#ea580c,#f97316)" : (isDark ? "#3b82f6" : "#93c5fd") }} />
                      </div>
                      <span style={{ width: 130, flexShrink: 0, fontSize: 11, color: ink2, textAlign: "right" }}>{s.score} · {s.appearances}/{s.possible}{s.avgRank != null ? ` · avg #${s.avgRank}` : ""}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Visibility matrix */}
          <div style={{ padding: "0 9px", marginBottom: 24 }}>
            <div className="cd-section">
              <div className="cd-section-hd"><span className="cd-hd-brand">Visibility Matrix — Who Models Recommend</span></div>
              <p className="cd-help">For each recommendation prompt, who each model named (in rank order). Your business is highlighted.</p>
              <div style={{ overflowX: "auto" }}>
                <table className="cd-history" style={{ margin: 0, width: "100%" }}>
                  <thead><tr><th style={{ textAlign: "left", minWidth: 200 }}>Prompt</th>{result.matrixModels.map(p => <th key={p} style={{ textAlign: "left" }}>{platLabel(p)}</th>)}</tr></thead>
                  <tbody>
                    {result.matrix.map((row, ri) => (
                      <tr key={ri}>
                        <td style={{ color: inkMid, fontSize: 11.5 }}>{row.prompt}</td>
                        {result.matrixModels.map(p => {
                          const cell = row.perPlatform[p];
                          const ranked = cell?.ranked ?? [];
                          return (
                            <td key={p} style={{ fontSize: 11.5 }}>
                              {ranked.length === 0 ? <span style={{ color: inkMuted }}>—</span> : (
                                <div style={{ display: "flex", flexDirection: "column", gap: 1 }}>
                                  {ranked.map((k, i) => { const e = result.entities.find(x => x.key === k); return <span key={k} style={{ color: e?.isYou ? accent : inkMid, fontWeight: e?.isYou ? 800 : 500 }}>{i + 1}. {entityName(k)}</span>; })}
                                </div>
                              )}
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          {/* Entity knowledge by model */}
          <div style={{ padding: "0 9px", marginBottom: 24 }}>
            <div className="cd-section">
              <div className="cd-section-hd"><span className="cd-hd-brand">Entity Knowledge by Model</span></div>
              <p className="cd-help">What each model knows about each business as an entity{result.knowledgeMode === "live" ? " (with live search)" : " (training data only)"}. Each business is its own section — your business first, then each competitor, divided by a line.</p>
              {result.entities.map((e, ei) => (
                <div key={e.key}>
                  {ei > 0 && <div style={{ borderTop: `2px solid ${cardBorder}`, margin: "18px 0" }} />}
                  <div style={{ fontSize: 13, fontWeight: 800, color: e.isYou ? accent : ink, marginBottom: 8 }}>{e.isYou ? "★ " : ""}{e.name}{e.location ? <span style={{ color: inkMuted, fontWeight: 400, fontSize: 12 }}> · {e.location}</span> : null}</div>
                  <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))", gap: 10, alignItems: "start" }}>
                    {result.knowledgeModels.map(p => {
                      const card = (result.knowledge[e.key] ?? []).find(c => c.platform === p);
                      return card ? <div key={p}>{renderCard(card)}</div> : (
                        <div key={p} style={{ border: `1px dashed ${boxBorder}`, borderRadius: 8, padding: "10px 12px" }}>
                          <div style={{ fontSize: 12, fontWeight: 800, color: ink, marginBottom: 4 }}>{platLabel(p)}</div>
                          <div style={{ fontSize: 11.5, color: inkMuted }}>No data.</div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Recommendations */}
          <div style={{ padding: "0 9px", marginBottom: 24 }}>
            <div className="cd-section">
              <div className="cd-section-hd"><span className="cd-hd-brand">Recommendations — Out-rank Your Competitors</span></div>
              {result.recommendations.length === 0 ? <p className="cd-help">No recommendations generated.</p> : (
                <div style={{ display: "flex", flexDirection: "column", gap: 10, marginTop: 6 }}>
                  {result.recommendations.map((r, i) => (
                    <div key={i} style={{ border: `1px solid ${cardBorder}`, borderLeft: `4px solid ${priColor(r.priority)}`, borderRadius: 8, background: cardBg, padding: "10px 14px" }}>
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8 }}>
                        <span style={{ fontSize: 13, fontWeight: 700, color: ink }}>{r.title}</span>
                        <span style={{ fontSize: 9.5, fontWeight: 700, textTransform: "uppercase", letterSpacing: .4, color: priColor(r.priority) }}>{r.priority}</span>
                      </div>
                      {r.why && <div style={{ fontSize: 12, color: ink2, marginTop: 4, lineHeight: 1.5 }}>{r.why}</div>}
                      {r.actions.length > 0 && <ul style={{ margin: "6px 0 0", paddingLeft: 18, display: "flex", flexDirection: "column", gap: 2 }}>{r.actions.map((a, j) => <li key={j} style={{ fontSize: 12, color: inkMid }}>{a}</li>)}</ul>}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}

const inp: React.CSSProperties = {
  width: "100%", boxSizing: "border-box", padding: "7px 10px", border: "1px solid #cbd5e1", borderRadius: 6,
  fontSize: 12.5, fontFamily: "'Inter','Mulish',sans-serif", color: "#0f172a", outline: "none",
};
